import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, Sparkles } from 'lucide-react';
import { apiClient } from '../../utils/apiClient';

/**
 * ImageRemixFullscreen - FLUX Kontext Pro Remix
 * Mit Before/After Slider für Vorher/Nachher Vergleich
 */
const ImageRemixFullscreen = ({
  isOpen,
  onClose,
  sourceImage,
  avatarId,
  trainingId,
  onRemixComplete,
  onVideoGenerate,
}) => {
  const scrollYRef = useRef(0);
  const inputRef = useRef(null);
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const sliderRef = useRef(null);
  
  const [changePrompt, setChangePrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [resultImages, setResultImages] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [error, setError] = useState(null);
  const [numImages, setNumImages] = useState(1);
  
  // Before/After Slider
  const [sliderPosition, setSliderPosition] = useState(50); // Prozent
  const [isDraggingSlider, setIsDraggingSlider] = useState(false);
  
  const [displayProgress, setDisplayProgress] = useState(0);
  const progressIntervalRef = useRef(null);

  // Animation
  const dotSize = 2;
  const dotSpacing = 8;
  const blurAmount = isGenerating ? Math.max(0, 15 - (displayProgress / 100) * 15) : 0;
  const dotOpacity = isGenerating ? Math.max(0, 0.6 - (displayProgress / 100) * 0.6) : 0;

  useEffect(() => {
    if (isOpen && sourceImage) {
      setCurrentImage(sourceImage);
      setResultImages([]);
      setCurrentSlide(0);
      setChangePrompt('');
      setError(null);
      setDisplayProgress(0);
      setSliderPosition(50);
    }
  }, [isOpen, sourceImage?.url]);

  useEffect(() => {
    if (!isOpen) return;
    scrollYRef.current = window.scrollY;
    
    const style = document.createElement('style');
    style.id = 'remix-modal-style';
    style.textContent = `
      body.remix-modal-open {
        position: fixed !important;
        top: -${scrollYRef.current}px !important;
        left: 0 !important;
        right: 0 !important;
        overflow: hidden !important;
        width: 100% !important;
        overscroll-behavior: none !important;
      }
      html.remix-modal-open {
        overflow: hidden !important;
        overscroll-behavior: none !important;
      }
    `;
    document.head.appendChild(style);
    document.body.classList.add('remix-modal-open');
    document.documentElement.classList.add('remix-modal-open');
    
    return () => {
      document.body.classList.remove('remix-modal-open');
      document.documentElement.classList.remove('remix-modal-open');
      const styleEl = document.getElementById('remix-modal-style');
      if (styleEl) styleEl.remove();
      window.scrollTo(0, scrollYRef.current);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current && !isGenerating) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen, isGenerating]);

  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  // Slider Touch/Mouse Handlers
  const handleSliderStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingSlider(true);
  };

  const handleSliderMove = useCallback((e) => {
    if (!isDraggingSlider || !containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const x = clientX - rect.left;
    const percent = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percent);
  }, [isDraggingSlider]);

  const handleSliderEnd = useCallback(() => {
    setIsDraggingSlider(false);
  }, []);

  useEffect(() => {
    if (isDraggingSlider) {
      window.addEventListener('mousemove', handleSliderMove);
      window.addEventListener('mouseup', handleSliderEnd);
      window.addEventListener('touchmove', handleSliderMove);
      window.addEventListener('touchend', handleSliderEnd);
      
      return () => {
        window.removeEventListener('mousemove', handleSliderMove);
        window.removeEventListener('mouseup', handleSliderEnd);
        window.removeEventListener('touchmove', handleSliderMove);
        window.removeEventListener('touchend', handleSliderEnd);
      };
    }
  }, [isDraggingSlider, handleSliderMove, handleSliderEnd]);

  const handleClose = useCallback(() => {
    if (isGenerating) return;
    onClose();
  }, [isGenerating, onClose]);

  const startProgressAnimation = () => {
    setDisplayProgress(0);
    progressIntervalRef.current = setInterval(() => {
      setDisplayProgress(prev => {
        if (prev >= 95) return 95;
        // Langsamer Progress: ~90 Sek bis 95% (für Gemini + ggf. Grok Fallback)
        const increment = prev < 30 ? 0.4 : prev < 60 ? 0.2 : prev < 80 ? 0.1 : 0.03;
        return Math.min(prev + increment, 95);
      });
    }, 100);
  };

  const stopProgressAnimation = (success = true) => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    if (success) {
      setDisplayProgress(100);
      setTimeout(() => setDisplayProgress(0), 500);
    } else {
      setDisplayProgress(0);
    }
  };

  const handleGenerate = async () => {
    if (!changePrompt?.trim() || isGenerating) return;
    
    setIsGenerating(true);
    setError(null);
    startProgressAnimation();
    
    try {
      const imageUrl = currentImage?.url || sourceImage?.url;
      
      const requestBody = {
        source_image_url: imageUrl,
        change_prompt: changePrompt?.trim(),
        avatar_id: avatarId || trainingId,
        num_images: numImages
      };
      
      console.log('🎨 Starting remix:', requestBody);
      
      const response = await apiClient.post('/api/remix/kontext', requestBody);
      
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.generation_id) {
        console.log('✅ Remix started:', data.generation_id);
        const genId = data.generation_id;
        
        // Polling für Ergebnis (nutzt normale generations list)
        const pollForResult = async () => {
          try {
            const listResponse = await apiClient.get(`/api/generations/list?avatar_id=${avatarId}&limit=10`);
            const listData = await listResponse.json();
            
            // Suche nach unserer Generation
            const found = listData.generations?.find(g => g.id === genId || g._id === genId);
            
            // Check auf FAILED Status
            if (found && found.status === 'failed') {
              clearInterval(pollInterval);
              stopProgressAnimation(false);
              setIsGenerating(false);
              setError(found.error || 'Remix fehlgeschlagen - bitte anderen Prompt versuchen');
              return;
            }
            
            if (found && found.image_urls?.length > 0) {
              // Fertig!
              clearInterval(pollInterval);
              
              const newImages = found.image_urls.map((url, idx) => ({
                url,
                id: `remix-${Date.now()}-${idx}`
              }));
              
              console.log('🎨 Remix fertig! Neue URLs:', found.image_urls);
              
              // 🚀 Pre-load das Bild SOFORT im Hintergrund
              const preloadImg = new Image();
              preloadImg.crossOrigin = 'anonymous';
              
              const showResult = () => {
                console.log('🖼️ Bild geladen, zeige Ergebnis:', newImages[0].url);
                stopProgressAnimation(true);
                setIsGenerating(false);
                setChangePrompt('');
                setResultImages(newImages);
                setCurrentImage(newImages[0]);
                setCurrentSlide(0);
                setSliderPosition(50);
                
                // Callback
                if (onRemixComplete) {
                  onRemixComplete(newImages[0]);
                }
              };
              
              preloadImg.onload = showResult;
              preloadImg.onerror = () => {
                console.warn('⚠️ Pre-load fehlgeschlagen, zeige trotzdem');
                showResult();
              };
              
              // Fallback: Nach 500ms trotzdem anzeigen
              setTimeout(() => {
                if (isGenerating) {
                  console.warn('⚠️ Pre-load Timeout, zeige trotzdem');
                  showResult();
                }
              }, 500);
              
              preloadImg.src = newImages[0].url;
            }
          } catch (err) {
            console.error('Poll error:', err);
          }
        };
        
        // Poll alle 500ms (schneller!)
        const pollInterval = setInterval(pollForResult, 500);
        pollForResult(); // Sofort einmal
        
        // Cleanup nach 2 Minuten
        setTimeout(() => {
          clearInterval(pollInterval);
          if (isGenerating) {
            stopProgressAnimation(false);
            setIsGenerating(false);
            setError('Timeout - bitte Galerie prüfen');
          }
        }, 120000);
        
        return;
      }
      
      throw new Error(data.error || 'Remix konnte nicht gestartet werden');
      
    } catch (err) {
      console.error('[Remix] Error:', err);
      stopProgressAnimation(false);
      setError(err.message || 'Remix fehlgeschlagen');
      setIsGenerating(false);
    }
  };
  

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !isGenerating) {
      e.preventDefault();
      handleGenerate();
    }
  };

  if (!isOpen) return null;

  const hasResults = resultImages.length > 0;
  const originalUrl = sourceImage?.url;
  const resultUrl = hasResults ? resultImages[currentSlide]?.url : null;

  const content = (
    <div 
      ref={containerRef}
      className="fixed inset-0 bg-black"
      style={{
        zIndex: 99999,
        overscrollBehavior: 'none',
        touchAction: isDraggingSlider ? 'none' : 'auto',
      }}
    >
      {/* Background Image / Before-After Comparison */}
      <div className="absolute inset-0">
        {hasResults && originalUrl && resultUrl ? (
          // Before/After Slider View
          <>
            {/* NEW Image (Full) - Right Side */}
            <img
              src={resultUrl}
              alt="Remix Result"
              className="absolute inset-0 w-full h-full object-contain"
              draggable={false}
            />
            
            {/* ORIGINAL Image (Clipped) - Left Side */}
            <div 
              className="absolute inset-0 overflow-hidden"
              style={{ width: `${sliderPosition}%` }}
            >
              <img
                src={originalUrl}
                alt="Original"
                className="absolute inset-0 w-full h-full object-contain"
                style={{ 
                  width: `${100 / (sliderPosition / 100)}%`,
                  maxWidth: 'none'
                }}
                draggable={false}
              />
            </div>
            
            {/* Slider Line + Handle */}
            <div
              ref={sliderRef}
              className="absolute top-0 bottom-0 cursor-ew-resize"
              style={{ 
                left: `${sliderPosition}%`,
                transform: 'translateX(-50%)',
                width: '44px',
                zIndex: 20
              }}
              onMouseDown={handleSliderStart}
              onTouchStart={handleSliderStart}
            >
              {/* Vertical Line */}
              <div 
                className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-white shadow-lg"
                style={{ transform: 'translateX(-50%)' }}
              />
              
              {/* Circle Handle */}
              <div 
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-xl flex items-center justify-center"
              >
                <div className="flex items-center gap-0.5 text-gray-600">
                  <span className="text-sm">◀</span>
                  <span className="text-sm">▶</span>
                </div>
              </div>
            </div>
            
                      </>
        ) : (
          // Normal Single Image View
          <>
            <img
              ref={imageRef}
              src={currentImage?.url || sourceImage?.url}
              alt="Remix"
              className="w-full h-full object-contain"
              style={{
                filter: isGenerating ? `blur(${blurAmount}px)` : 'none',
                transition: 'filter 0.5s ease',
              }}
              draggable={false}
            />
            
            {/* Dot Grid Overlay */}
            {isGenerating && dotOpacity > 0 && (
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  backgroundImage: `radial-gradient(circle, rgba(255,255,255,${dotOpacity}) ${dotSize}px, transparent ${dotSize}px)`,
                  backgroundSize: `${dotSpacing}px ${dotSpacing}px`,
                }}
              />
            )}
          </>
        )}
      </div>

      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 p-4 pt-safe">
        <button
          onClick={handleClose}
          disabled={isGenerating}
          className="w-10 h-10 rounded-full bg-black/50 backdrop-blur flex items-center justify-center active:scale-95 disabled:opacity-50"
        >
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Bottom Controls */}
      <div 
        className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black via-black/90 to-transparent pb-8 pt-4"
        style={{ overscrollBehavior: 'none' }}
      >
        <div className="px-4 space-y-3">
          {/* Progress - EXAKT wie Video */}
          {isGenerating && (
            <div className="space-y-3">
              {/* Progress Bar + Percentage */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-1.5 bg-white/20 rounded-full overflow-hidden">
                  <div
                    className={`h-full bg-[#10B981] transition-all duration-200 ease-out ${
                      displayProgress >= 80 ? 'animate-pulse' : ''
                    }`}
                    style={{ width: `${Math.round(displayProgress)}%` }}
                  />
                </div>
                <span className={`text-white font-bold text-sm text-right ${
                  displayProgress >= 80 ? 'animate-pulse' : ''
                }`}>
                  {displayProgress >= 80 ? 'Optimiere...' : `${Math.round(displayProgress)}%`}
                </span>
              </div>

              {/* Spinner zentriert */}
              <div className="flex justify-center">
                <div className="w-4 h-4 border-2 border-[#10B981] border-t-transparent rounded-full animate-spin" />
              </div>
            </div>
          )}

          {/* Error */}
          {error && !isGenerating && (
            <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-3">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Input */}
          {!isGenerating && !hasResults && (
            <>
              <div className="text-center text-white/50 text-xs">
                ✨ Beschreibe die Änderung - KI versteht den Kontext
              </div>
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={changePrompt}
                  onChange={(e) => setChangePrompt(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="z.B. rote Haare, Sonnenbrille, lächeln..."
                  className="flex-1 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-white/40"
                />
                <button
                  onClick={handleGenerate}
                  disabled={!changePrompt?.trim() || isGenerating}
                  className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-xl border border-white/20 flex items-center justify-center disabled:opacity-40 active:scale-95"
                >
                  <Sparkles className="w-5 h-5 text-white" />
                </button>
              </div>
            </>
          )}

          {/* Results - Before/After Info */}
          {hasResults && !isGenerating && (
            <>
              <div className="text-center text-white/50 text-xs">
                ← Slider ziehen für Vorher/Nachher Vergleich →
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setResultImages([]);
                    setCurrentImage(sourceImage);
                    setChangePrompt('');
                    setSliderPosition(50);
                  }}
                  className="flex-1 py-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl text-white font-medium active:scale-98"
                >
                  Neuer Remix
                </button>
                {onVideoGenerate && (
                  <button
                    onClick={() => onVideoGenerate({ url: resultUrl })}
                    className="flex-1 py-3 bg-white/20 backdrop-blur-xl border border-white/20 rounded-xl text-white font-medium active:scale-98"
                  >
                    🎬 Video
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default ImageRemixFullscreen;
